import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, Renderer2, signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';

import { IIFrameBlock } from './iframe.model';
import { environment } from '../../../../../../environments/environment';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';

@Component({
    selector: 'iframe[app-iframe]',
    imports: [],
    templateUrl: './iframe.component.html',
    styleUrl: './iframe.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'app-iframe',
        '[class]': 'classes()',
        '(load)': 'onLoad()',
        allow: 'payment',
    },
})
export class IframeComponent {
    private _tailwind = inject(TailwindService);
    private _route = inject(ActivatedRoute);
    private _el = inject<ElementRef<HTMLIFrameElement>>(ElementRef);
    private _renderer = inject(Renderer2);
    private _queryParams = toSignal(this._route.queryParams, { initialValue: {} as Params });
    loading = signal(true);

    block = input.required<IIFrameBlock>();
    classes = computed(() => {
        const block = this.block();
        return [...this._tailwind.getSpacingClasses(block)];
    });
    private _isConfigurator = computed<boolean>(() => {
        const block = this.block();
        switch (block.category) {
            case 'general_configurator':
                return true;
            case 'dog':
                return block.option_dog === 'dog_configurator';
            case 'cat':
                return block.option_cat === 'cat_configurator';
            case 'horse':
                return block.option_horse === 'horse_configurator';
            default:
                return false;
        }
    });
    sandbox = computed<string | null>(() => {
        // Legacy (non-configurator) online applications must run with NO sandbox attribute at all,
        // otherwise their PayPal redirect flow breaks (per Barmenia). Returning null drops the attribute.
        // The configurator keeps a full sandbox including popups for the new PayPal flow.
        if (!this._isConfigurator()) return null;
        return 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals allow-downloads allow-storage-access-by-user-activation';
    });
    private _rawLink = computed<string | undefined>(() => {
        const blok = this.block();
        switch (blok.category) {
            case 'dog':
                return environment.iframes['dog'][blok.option_dog];
            case 'cat':
                return environment.iframes['cat'][blok.option_cat];
            case 'horse':
                return environment.iframes['horse'][blok.option_horse];
            case 'general':
                return environment.iframes['general'];
            case 'general_configurator':
                return environment.preview ? environment.iframes['testing_url'] : environment.iframes['general_configurator'];
        }
    });
    link = computed<string | undefined>(() => {
        const raw = this._rawLink();
        if (!raw) return undefined;
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(this._queryParams())) {
            if (key.startsWith('_storyblok')) continue;
            for (const entry of Array.isArray(value) ? value : [value]) {
                if (entry != null) search.append(key, entry);
            }
        }
        const query = search.toString();
        return query ? `${raw}${raw.includes('?') ? '&' : '?'}${query}` : raw;
    });

    constructor() {
        // `sandbox` is a security-sensitive <iframe> attribute: Angular forbids binding it (NG0910)
        // because a binding may land after `src` and the browser would ignore it. Setting it through
        // the renderer, strictly before `src`, keeps the flags in place for the iframe's first load.
        effect(() => {
            const el = this._el.nativeElement;
            const sandbox = this.sandbox();
            const src = this.link();
            if (sandbox === null) {
                this._renderer.removeAttribute(el, 'sandbox');
            } else {
                this._renderer.setAttribute(el, 'sandbox', sandbox);
            }
            if (src) {
                this._renderer.setAttribute(el, 'src', src);
            } else {
                this._renderer.removeAttribute(el, 'src');
            }
        });
    }

    onLoad(): void {
        this.loading.set(false);
    }
}
